<template>
  <wz-layout
    :theme="settingsValues.themeMode"
    :navigation-style="settingsValues.navigationStyle"
  >

    <template #sidebar="{ collapsed }">
      <wz-sidebar
        v-if="settingsValues.navigationStyle === 'vertical'"
        :items="menuItems"
        :collapsed="collapsed"
        logo-text="Crystal"
        :active-key="currentRoute"
        :resolve-href="resolveHref"
        @item-click="navigate"
      >
        <template #logo>
          <CrystalLogo :size="collapsed ? 32 : 36" />
          <span v-if="!collapsed" class="sidebar-brand-text">Crystal</span>
        </template>
      </wz-sidebar>
    </template>

    <template #header="{ toggleSidebar, collapsed }">
      <wz-header
        :collapsed="collapsed"
        :user="userObject"
        :layout-mode="settingsValues.navigationStyle"
        :action-items="actionItems"
        :user-menu-items="userMenuItems"
        @menu-toggle="toggleSidebar"
        @settings="isSettingsOpen = true"
        @action-click="handleActionClick"
        @user-menu-click="handleUserMenuClick"
      >
        <template #logo>
          <CrystalLogo :size="36" />
          <span class="wz-header-dynamic-logo-text">Crystal</span>
        </template>
      </wz-header>
    </template>

    <template #horizontal-nav>
      <wz-horizontal-nav
        v-if="settingsValues.navigationStyle === 'horizontal'"
        :items="menuItems"
        :active-key="currentRoute"
        :resolve-href="resolveHref"
        @item-click="navigate"
      />
    </template>

    <div class="app-content">
      <slot></slot>
    </div>

    <wz-settings-panel
      :is-open="isSettingsOpen"
      v-model="settingsValues"
      @close="isSettingsOpen = false"
    />

  </wz-layout>
</template>

<script>
import { mapGetters } from 'vuex'
import { LocalStorage } from "@wizepe/wizeui"
import { SecurityService } from '@/core/services'
import CrystalLogo from '@/core/components/icons/CrystalLogo.vue'

export default {
  name: 'PageBase',
  components: { CrystalLogo },
  data() {
    return {
      currentRoute: '',
      isSettingsOpen: false,
      isFullscreen: false,
      settingsValues: {
        themeMode: 'light',
        navigationStyle: 'vertical',
      },
      menuItems: [
        { type: 'group', label: 'Principal' },
        { key: 'dashboard',        label: 'Portada',        icon: 'ri-home-5-line' },
        { key: 'employee-list',    label: 'Colaboradores',  icon: 'ri-file-user-line' },
        {
          key: 'contracts',
          label: 'Contratos',
          icon: 'ri-file-list-line',
          children: [
            { key: 'contracts-list',         label: 'Lista' },
            { key: 'contract-template-list', label: 'Plantillas' },
          ],
        },
        { key: 'medical-leave-list', label: 'Descansos médicos', icon: 'ri-heart-pulse-line' },
        { key: 'licenses-list', label: 'Licencias y permisos', icon: 'ri-file-shield-line' },
        { key: 'gratification-list', label: 'Gratificaciones', icon: 'ri-money-dollar-circle-line' },
        { key: 'cts-list', label: 'CTS', icon: 'ri-safe-2-line' },
        { key: 'severance-list', label: 'Liquidaciones', icon: 'ri-file-list-3-line' },
        {
          key: 'vacations',
          label: 'Vacaciones',
          icon: 'ri-calendar-check-line',
          children: [
            { key: 'vacation-requests-list', label: 'Solicitudes' },
            { key: 'vacation-calendar',      label: 'Calendario' },
            { key: 'vacation-history',       label: 'Historial' },
            { key: 'vacation-periods-list',  label: 'Períodos' },
          ],
        },
        {
          key: 'attendance',
          label: 'Asistencia',
          icon: 'ri-time-line',
          children: [
            { key: 'attendance-grid',    label: 'Control mensual' },
            { key: 'attendance-summary', label: 'Resumen' },
          ],
        },
        {
          key: 'payrolls-parent',
          label: 'Planillas',
          icon: 'ri-wallet-3-line',
          children: [
            { key: 'payroll-engine-list',   label: 'Planillas' },
            { key: 'payroll-movement-list', label: 'Descuentos y adicionales' },
            { key: 'payroll-base-batch',    label: 'Cargas externas' },
          ],
        },
        { key: 'operational-payment-list', label: 'Pagos Operativos', icon: 'ri-hand-coin-line' },
        { key: 'legajo-dashboard', label: 'Legajo documental', icon: 'ri-folder-shield-2-line' },
        { key: 'signature-inbox', label: 'Firmas por firmar', icon: 'ri-quill-pen-line' },
        { type: 'group', label: 'Configuración' },
        {
          key: 'config',
          label: 'Mantenimiento',
          icon: 'ri-settings-3-line',
          children: [
            { key: 'company-list',         label: 'Empresas' },
            { key: 'work-site-list',       label: 'Sedes' },
            { key: 'bank-list',            label: 'Bancos' },
            { key: 'payroll-concept-list', label: 'Conceptos remunerativos' },
            { key: 'area-list',            label: 'Áreas' },
            { key: 'position-list',        label: 'Cargos' },
            { key: 'channel-list',         label: 'Canales' },
            { key: 'expense-line-list',    label: 'Líneas de gasto' },
            { key: 'pension-entity-list',  label: 'Entidades pensionarias' },
            { key: 'document-type-list',   label: 'Tipos de documento' },
            { key: 'cessation-reason-list', label: 'Motivos de cese' },
            { key: 'leave-type-list',      label: 'Tipos de licencia' },
            { key: 'payroll-type-list',    label: 'Tipos de planilla' },
            { key: 'operational-payment-concept-list', label: 'Conceptos de pago operativo' },
            { key: 'legal-parameter-list', label: 'Parámetros legales' },
            { key: 'custom-field-list',    label: 'Campos personalizados' },
            { key: 'signer-role-list',     label: 'Roles de firmante' },
            { key: 'work-schedule-list',   label: 'Horarios' },
          ],
        },
        { type: 'group', label: 'Sistema' },
        { key: 'audit-log', label: 'Auditoría', icon: 'ri-edit-line' },
      ],
      userMenuItems: [
        { key: 'profile',  label: 'Mi perfil',      icon: 'ri-user-line' },
        { key: 'settings', label: 'Configuración',   icon: 'ri-settings-3-line' },
        { key: 'support',  label: 'Soporte',         icon: 'ri-headphone-line' },
        { key: 'logout',   label: 'Cerrar sesión',   icon: 'ri-logout-box-line', isLogout: true },
      ],
    }
  },
  computed: {
    ...mapGetters({
      user: 'auth/getUser',
    }),
    userName() {
      if (!this.user) return ''
      const { name, firstName, lastName, email } = this.user
      return name || [firstName, lastName].filter(Boolean).join(' ') || email || ''
    },
    userObject() {
      return { name: this.userName, avatar: this.user?.avatar || null }
    },
    actionItems() {
      const isDark = this.settingsValues.themeMode === 'dark'
      return [
        { key: 'theme',      icon: isDark ? 'ri-sun-line' : 'ri-moon-line' },
        { key: 'fullscreen', icon: this.isFullscreen ? 'ri-fullscreen-exit-line' : 'ri-fullscreen-line' },
      ]
    },
  },
  watch: {
    $route(to) {
      this.currentRoute = to.name
    },
    settingsValues: {
      deep: true,
      handler(val) {
        localStorage.setItem('crystalSettings', JSON.stringify(val))
      },
    },
  },
  methods: {
    navigate(item) {
      if (this.$route.name === item.key) return
      try {
        this.$router.push({ name: item.key })
      } catch (_) { /* navegación duplicada ignorada */ }
    },
    resolveHref(item) {
      if (!item || !item.key) return null
      try {
        return this.$router.resolve({ name: item.key }).href
      } catch (_) {
        return null
      }
    },
    handleActionClick(action) {
      if (action.key === 'theme') {
        this.settingsValues.themeMode = this.settingsValues.themeMode === 'light' ? 'dark' : 'light'
      }
      if (action.key === 'fullscreen') {
        if (!document.fullscreenElement) {
          document.documentElement.requestFullscreen().catch(err => console.error(err))
        } else {
          document.exitFullscreen()
        }
      }
    },
    async handleUserMenuClick(item) {
      if (item.key === 'logout') {
        try {
          await this.$store.dispatch('app/loading', true)
          await SecurityService.logout({ refreshToken: LocalStorage.getRefreshToken() })
        } catch (e) {
          console.error(e)
        } finally {
          await this.$store.dispatch('app/loading', false)
          this.$store.dispatch('auth/logout').then(() => this.$router.push({ name: 'login' }))
        }
      }
    },
    onFullscreenChange() {
      this.isFullscreen = !!document.fullscreenElement
    },
  },
  mounted() {
    if (this.$route && this.$route.name) {
      this.currentRoute = this.$route.name
    }
    const saved = localStorage.getItem('crystalSettings')
    if (saved) {
      this.settingsValues = JSON.parse(saved)
    }
    document.addEventListener('fullscreenchange', this.onFullscreenChange)
  },
  beforeDestroy() {
    document.removeEventListener('fullscreenchange', this.onFullscreenChange)
  },
}
</script>

<style scoped>
.sidebar-brand-text {
  font-family: 'Quicksand', sans-serif;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: var(--admin-text-main, #1f2937);
  text-transform: uppercase;
}
</style>
